import React, { useState } from 'react';
import SEO from '../../../component/common/SEO';
import { pageSEO } from '../../../config/seoConfig';
import { SlLocationPin } from 'react-icons/sl';
import { GoMail } from 'react-icons/go';
import { IoCallOutline } from 'react-icons/io5';
import { ToastTypes, useMessage } from '../../../base/context/MessageProvider';
import { useLoader } from '../../../base/context/LoaderProvider';
import { ContactApiService } from '../../../service/website/contactApiService';
import { emailRegex } from '../../../helper/regex';
import './style.css';

export default function ContactUs() {
  const [data, setData] = useState({
    name: '',
    phone: '',
    email: '',
    orderId: '',
    subject: '',
    classification: '',
    description: '',
  });

  const [error, setError] = useState({
    name: '',
    phone: '',
    email: '',
    orderId: '',
    subject: '',
    classification: '',
    description: '',
  });

  const message = useMessage();
  const loader = useLoader();

  const handleOnChange = (e) => {
    const { name, value } = e.target;
    setData((prev) => ({ ...prev, [name]: value }));
    setError((prev) => ({ ...prev, [name]: '' }));
  };

  const validate = () => {
    let valid = true;
    const nextErrors = {};

    if (!data.name.trim()) {
      nextErrors.name = 'Name is required';
      valid = false;
    }

    if (!data.phone.trim()) {
      nextErrors.phone = 'Phone number is required';
      valid = false;
    } else if (!/^[\d\-]{10,15}$/.test(data.phone.trim())) {
      nextErrors.phone = 'Please enter a valid phone number';
      valid = false;
    }

    if (!data.email.trim()) {
      nextErrors.email = 'Email is required';
      valid = false;
    } else if (!emailRegex.test(data.email.trim())) {
      nextErrors.email = 'Not a valid email address';
      valid = false;
    }

    if (!data.subject.trim()) {
      nextErrors.subject = 'Subject is required';
      valid = false;
    }

    if (!data.classification) {
      nextErrors.classification = 'Issue category is required';
      valid = false;
    }

    if (!data.description.trim()) {
      nextErrors.description = 'Description is required';
      valid = false;
    } else if (data.description.trim().length < 10) {
      nextErrors.description = 'Please provide at least 10 characters';
      valid = false;
    }

    setError((prev) => ({ ...prev, ...nextErrors }));
    return valid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    const payload = {
      name: data.name,
      phone: data.phone,
      email: data.email,
      orderId: data.orderId,
      subject: data.subject,
      classification: data.classification,
      description: data.description,
    };

    try {
      loader.showButtonLoader('Sending message...', 'small');
      const res = await ContactApiService.contactToAdmin(JSON.stringify(payload));
      loader.hideLoader();
      if (res?.status?.code === 200) {
        message.showToast(res.status.message, ToastTypes.SUCCESS);
        setData({
          name: '',
          phone: '',
          email: '',
          orderId: '',
          subject: '',
          classification: '',
          description: '',
        });
      } else {
        message.showToast(res?.error?.message || 'Something went wrong', ToastTypes.ERROR);
      }
    } catch (err) {
      loader.hideLoader();
      message.showToast(err?.message || 'Server error. Try again later', ToastTypes.ERROR);
    }
  };

  const handlePhoneInput = (e) => {
    const x = e.target.value.replace(/\D/g, '');
    if (x.length <= 3) e.target.value = x;
    else if (x.length <= 6) e.target.value = `${x.slice(0, 3)}-${x.slice(3)}`;
    else e.target.value = `${x.slice(0, 3)}-${x.slice(3, 6)}-${x.slice(6, 10)}`;
    setData((prev) => ({ ...prev, phone: e.target.value }));
    setError((prev) => ({ ...prev, phone: '' }));
  };

  return (
    <div className="importerr-contact-page">
      <SEO {...pageSEO.contact} />

      {/* <div className="importerr-contact-banner">
         <h1>Contact Us</h1>
      </div> */}

      {/* New two-column card: hero (left) + form (right) */}
      <div className="importerr-support-form">
        {/* HERO */}
        <div className="importerr-support-hero">
          <div className="importerr-hero-content">
            <h1>We&apos;re here to help you</h1>
            <p>
              Our support team is ready to assist you with any questions or issues you may
              have.
            </p>

            <div className="importerr-contact-methods">
              <div className="importerr-contact-item">
                <div className="importerr-contact-icon">
                  <GoMail />
                </div>
                <div className="importerr-contact-info">
                  <h4>Email us</h4>
                  <a href="mailto:contact@importerr.com">contact@importerr.com</a>
                </div>
              </div>

              <div className="importerr-contact-item">
                <div className="importerr-contact-icon">
                  <IoCallOutline />
                </div>
                <div className="importerr-contact-info">
                  <h4>Call us</h4>
                  <a href="tel:+917827842406">+91 7827842406</a>
                </div>
              </div>

              <div className="importerr-contact-item">
                <div className="importerr-contact-icon">
                  <SlLocationPin />
                </div>
                <div className="importerr-contact-info">
                  <h4>Visit us</h4>
                  <p>Boundless Trends Pvt. Ltd. <br></br>B-71, Sector-6, Noida, Gautam Buddha Nagar, Uttar Pradesh, 201301</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* FORM */}
        <div className="importerr-support-form-container">
          <div className="importerr-form-header">
            <h2>Contact Support</h2>
            <p>Fill out the form and our team will get back to you within 24 hours.</p>
          </div>

          <form className="importerr-form" onSubmit={handleSubmit} noValidate>
            {/* Name + Phone */}
            <div className="importerr-form-row">
              <div className="importerr-input-group">
                <label className="importerr-form-label required">Name</label>
                <input
                  type="text"
                  name="name"
                  placeholder=" "
                  value={data.name}
                  onChange={handleOnChange}
                  aria-invalid={!!error.name}
                />
                {error.name && <div className="importerr-error-message">{error.name}</div>}
              </div>

              <div className="importerr-input-group">
                <label className="importerr-form-label required">Phone Number</label>
                <input
                  type="tel"
                  name="phone"
                  placeholder=" "
                  value={data.phone}
                  onChange={handlePhoneInput}
                  aria-invalid={!!error.phone}
                />
                {error.phone && <div className="importerr-error-message">{error.phone}</div>}
              </div>
            </div>

            {/* Email + Order ID */}
            <div className="importerr-form-row">
              <div className="importerr-input-group">
                <label className="importerr-form-label required">Email</label>
                <input
                  type="email"
                  name="email"
                  placeholder=" "
                  value={data.email}
                  onChange={handleOnChange}
                  aria-invalid={!!error.email}
                />
                {error.email && <div className="importerr-error-message">{error.email}</div>}
              </div>

              <div className="importerr-input-group">
                <label className="importerr-form-label">Order ID</label>
                <input
                  type="text"
                  name="orderId"
                  placeholder=" "
                  value={data.orderId}
                  onChange={handleOnChange}
                  aria-invalid={!!error.orderId}
                />
                {error.orderId && <div className="importerr-error-message">{error.orderId}</div>}
              </div>
            </div>

            {/* Subject */}
            <div className="importerr-form-row single">
              <div className="importerr-input-group">
                <label className="importerr-form-label required">Subject</label>
                <input
                  type="text"
                  name="subject"
                  placeholder=" "
                  value={data.subject}
                  onChange={handleOnChange}
                  aria-invalid={!!error.subject}
                />
                {error.subject && <div className="importerr-error-message">{error.subject}</div>}
              </div>
            </div>

            {/* Issue Category */}
            <div className="importerr-form-row single">
              <div className="importerr-input-group">
                <label className="importerr-form-label required">Issue Category</label>
                <select
                  name="classification"
                  value={data.classification}
                  onChange={handleOnChange}
                  aria-invalid={!!error.classification}
                >
                  <option value="" disabled>
                    Select Issue Category
                  </option>
                  <option value="Order Related">Order Related</option>
                  <option value="Payment Issue">Payment Issue</option>
                  <option value="Product Issue">Product Issue</option>
                  <option value="Shipping/Delivery Delay">Shipping/Delivery</option>
                  <option value="Return/Refund Request">Return/Refund/Exchange</option>
                  <option value="Technical Support">Technical Support</option>
                  <option value="Account/Login Issue">Account/Login Issue</option>
                  <option value="Counterfeit / IP Infringement">Counterfeit / IP Infringement</option>
                  <option value="Other">Other</option>
                </select>
                {error.classification && (
                  <div className="importerr-error-message">{error.classification}</div>
                )}
              </div>
            </div>

            {/* Description */}
            <div className="importerr-form-row single">
              <div className="importerr-input-group">
                <label className="importerr-form-label required">Description</label>
                <textarea
                  name="description"
                  placeholder=" "
                  value={data.description}
                  onChange={handleOnChange}
                  aria-invalid={!!error.description}
                />
                {error.description && (
                  <div className="importerr-error-message">{error.description}</div>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="importerr-form-actions">
              <button
                type="button"
                className="importerr-btn importerr-btn-secondary"
                onClick={() => {
                  setData({
                    name: '',
                    phone: '',
                    email: '',
                    orderId: '',
                    subject: '',
                    classification: '',
                    description: '',
                  });
                  setError({});
                }}
              >
                Reset
              </button>
              <button type="submit" className="importerr-btn importerr-btn-primary">
                Submit Request
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
